import React, { useState, useEffect } from 'react';
import {
    Grid,
    Card,
    CardContent,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Chip,
    Box,
    Button,
    TextField,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    FormControlLabel,
    Switch,
    IconButton,
    Alert,
    LinearProgress,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Tooltip,
    Paper
} from '@mui/material';
import {
    Add as AddIcon,
    Delete as DeleteIcon,
    Visibility as ViewIcon,
    PlayArrow as DeployIcon,
    Refresh as RefreshIcon,
    ExpandMore as ExpandMoreIcon,
    Timeline as TimelineIcon,
    Speed as SpeedIcon,
    CheckCircle as HealthyIcon,
    Error as ErrorIcon,
    Warning as WarningIcon
} from '@mui/icons-material';
import MainCard from 'ui-component/cards/MainCard';
import { useTheme } from '@mui/material/styles';
import {
    useGetModelServingListQuery,
    useGetModelServingTemplatesQuery,
    useDeployModelMutation,
    useUndeployModelMutation,
    useLazyGetModelLogsQuery,
    useHealthCheckModelMutation
} from 'store/slices/apiSlice';

const ModelServingDashboard = () => {
    const theme = useTheme();

    const { data: listData, isLoading: loading, error: listError, refetch: refetchList } = useGetModelServingListQuery(undefined, { skip: false });
    const { data: templatesData } = useGetModelServingTemplatesQuery(undefined, { skip: false });
    const [deployModel, { isLoading: deploying }] = useDeployModelMutation();
    const [undeployModel, { isLoading: undeploying }] = useUndeployModelMutation();
    const [getLogs, { data: logsData }] = useLazyGetModelLogsQuery();
    const [healthCheckModel, { isLoading: healthChecking }] = useHealthCheckModelMutation();

    const models = listData?.models ?? [];
    const templates = templatesData ?? [];
    const modelLogs = logsData?.logs ?? [];
    const error = listError?.data?.detail ?? listError?.message ?? null;
    const displayError = actionError ?? error;

    const [selectedModel, setSelectedModel] = useState(null);
    const [deployDialog, setDeployDialog] = useState(false);
    const [logsDialog, setLogsDialog] = useState(false);
    const [actionError, setActionError] = useState(null);

    // Deploy form state
    const [deployForm, setDeployForm] = useState({
        name: '',
        display_name: '',
        description: '',
        notebook_path: '',
        project_name: '',
        framework: 'custom_python',
        model_type: 'custom',
        version: '1.0.0',
        cpu_limit: 1.0,
        memory_limit: 512,
        timeout_seconds: 30,
        auth_required: true,
        rate_limit_tier: 'standard',
        requirements: ['fastapi>=0.75.0', 'uvicorn>=0.17.0'],
        environment_vars: {},
        input_schema: {},
        output_schema: {}
    });

    const handleDeploy = async () => {
        setActionError(null);
        try {
            const deployData = {
                ...deployForm,
                requirements: deployForm.requirements.filter((req) => req.trim()),
                input_schema:
                    typeof deployForm.input_schema === 'string' ? JSON.parse(deployForm.input_schema || '{}') : deployForm.input_schema,
                output_schema:
                    typeof deployForm.output_schema === 'string' ? JSON.parse(deployForm.output_schema || '{}') : deployForm.output_schema,
                environment_vars:
                    typeof deployForm.environment_vars === 'string'
                        ? JSON.parse(deployForm.environment_vars || '{}')
                        : deployForm.environment_vars
            };
            await deployModel(deployData).unwrap();
            setDeployDialog(false);
            resetDeployForm();
            refetchList();
        } catch (err) {
            setActionError(err?.data?.detail || 'Deployment failed');
        }
    };

    const handleUndeploy = async (modelId) => {
        if (!window.confirm('Are you sure you want to undeploy this model?')) return;
        setActionError(null);
        try {
            await undeployModel(modelId).unwrap();
            refetchList();
        } catch (err) {
            setActionError(err?.data?.detail || 'Failed to undeploy model');
        }
    };

    const handleViewLogs = async (modelId) => {
        setActionError(null);
        setLogsDialog(true);
        getLogs(modelId).catch((err) => setActionError(err?.data?.detail || 'Failed to load logs'));
    };

    const handleHealthCheck = async (modelId) => {
        setActionError(null);
        try {
            await healthCheckModel(modelId).unwrap();
            refetchList();
        } catch (err) {
            setActionError(err?.data?.detail || 'Health check failed');
        }
    };

    const resetDeployForm = () => {
        setDeployForm({
            name: '',
            display_name: '',
            description: '',
            notebook_path: '',
            project_name: '',
            framework: 'custom_python',
            model_type: 'custom',
            version: '1.0.0',
            cpu_limit: 1.0,
            memory_limit: 512,
            timeout_seconds: 30,
            auth_required: true,
            rate_limit_tier: 'standard',
            requirements: ['fastapi>=0.75.0', 'uvicorn>=0.17.0'],
            environment_vars: {},
            input_schema: {},
            output_schema: {}
        });
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'active':
                return 'success';
            case 'building':
                return 'warning';
            case 'deploying':
                return 'info';
            case 'error':
                return 'error';
            case 'inactive':
                return 'default';
            default:
                return 'default';
        }
    };

    const getHealthIcon = (isHealthy, status) => {
        if (status !== 'active') return <WarningIcon color="warning" />;
        return isHealthy ? <HealthyIcon color="success" /> : <ErrorIcon color="error" />;
    };

    if (loading && models.length === 0) {
        return (
            <Box sx={{ width: '100%', mt: 2 }}>
                <LinearProgress />
            </Box>
        );
    }

    return (
        <Box>
            <Typography variant="h4" gutterBottom>
                Model Serving Platform
            </Typography>
            <Typography variant="body1" color="textSecondary" sx={{ mb: 3 }}>
                Deploy and manage Jupyter notebooks as API endpoints
            </Typography>

            {displayError && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setActionError(null)}>
                    {displayError}
                </Alert>
            )}

            {/* Summary Cards */}
            <Grid container spacing={3} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6} md={3}>
                    <Card>
                        <CardContent>
                            <Box display="flex" alignItems="center">
                                <DeployIcon color="primary" sx={{ mr: 1 }} />
                                <Box>
                                    <Typography color="textSecondary" gutterBottom>
                                        Active Models
                                    </Typography>
                                    <Typography variant="h4">{models.filter((m) => m.status === 'active').length}</Typography>
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                    <Card>
                        <CardContent>
                            <Box display="flex" alignItems="center">
                                <HealthyIcon color="success" sx={{ mr: 1 }} />
                                <Box>
                                    <Typography color="textSecondary" gutterBottom>
                                        Healthy Models
                                    </Typography>
                                    <Typography variant="h4">{models.filter((m) => m.is_healthy).length}</Typography>
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                    <Card>
                        <CardContent>
                            <Box display="flex" alignItems="center">
                                <SpeedIcon color="info" sx={{ mr: 1 }} />
                                <Box>
                                    <Typography color="textSecondary" gutterBottom>
                                        Total Requests
                                    </Typography>
                                    <Typography variant="h4">
                                        {models.reduce((sum, m) => sum + (m.total_requests || 0), 0).toLocaleString()}
                                    </Typography>
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                    <Card>
                        <CardContent>
                            <Box display="flex" alignItems="center">
                                <TimelineIcon color="secondary" sx={{ mr: 1 }} />
                                <Box>
                                    <Typography color="textSecondary" gutterBottom>
                                        Available Templates
                                    </Typography>
                                    <Typography variant="h4">{templates.length}</Typography>
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* Deployed Models Table */}
            <MainCard
                title="Deployed Models"
                secondary={
                    <Box>
                        <Button variant="outlined" startIcon={<RefreshIcon />} onClick={() => refetchList()} sx={{ mr: 1 }}>
                            Refresh
                        </Button>
                        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setDeployDialog(true)}>
                            Deploy Model
                        </Button>
                    </Box>
                }
            >
                <TableContainer>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Name</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell>Framework</TableCell>
                                <TableCell>Version</TableCell>
                                <TableCell>Endpoint</TableCell>
                                <TableCell>Health</TableCell>
                                <TableCell>Created</TableCell>
                                <TableCell>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {models.map((model) => (
                                <TableRow key={model.id}>
                                    <TableCell>
                                        <Box>
                                            <Typography variant="subtitle2">{model.display_name}</Typography>
                                            <Typography variant="caption" color="textSecondary">
                                                {model.name}
                                            </Typography>
                                        </Box>
                                    </TableCell>
                                    <TableCell>
                                        <Chip label={model.status} color={getStatusColor(model.status)} size="small" />
                                    </TableCell>
                                    <TableCell>
                                        <Chip label={model.framework} variant="outlined" size="small" />
                                    </TableCell>
                                    <TableCell>{model.version}</TableCell>
                                    <TableCell>
                                        <Typography variant="body2" fontFamily="monospace">
                                            {model.endpoint_path}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Tooltip title={model.is_healthy ? 'Healthy' : 'Unhealthy'}>
                                            {getHealthIcon(model.is_healthy, model.status)}
                                        </Tooltip>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2">{new Date(model.created_at).toLocaleDateString()}</Typography>
                                    </TableCell>
                                    <TableCell>
                                        <IconButton size="small" onClick={() => handleViewLogs(model.id)} title="View Logs">
                                            <ViewIcon />
                                        </IconButton>
                                        <IconButton size="small" onClick={() => handleHealthCheck(model.id)} title="Health Check">
                                            <RefreshIcon />
                                        </IconButton>
                                        <IconButton size="small" color="error" onClick={() => handleUndeploy(model.id)} title="Undeploy">
                                            <DeleteIcon />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </MainCard>

            {/* Deploy Model Dialog */}
            <Dialog open={deployDialog} onClose={() => setDeployDialog(false)} maxWidth="md" fullWidth>
                <DialogTitle>Deploy Jupyter Notebook as API</DialogTitle>
                <DialogContent>
                    <Box sx={{ mt: 1 }}>
                        <Grid container spacing={2}>
                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    label="Model Name"
                                    value={deployForm.name}
                                    onChange={(e) => setDeployForm({ ...deployForm, name: e.target.value })}
                                    sx={{ mb: 2 }}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    label="Display Name"
                                    value={deployForm.display_name}
                                    onChange={(e) => setDeployForm({ ...deployForm, display_name: e.target.value })}
                                    sx={{ mb: 2 }}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Description"
                                    multiline
                                    rows={2}
                                    value={deployForm.description}
                                    onChange={(e) => setDeployForm({ ...deployForm, description: e.target.value })}
                                    sx={{ mb: 2 }}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    label="Notebook Path"
                                    value={deployForm.notebook_path}
                                    onChange={(e) => setDeployForm({ ...deployForm, notebook_path: e.target.value })}
                                    sx={{ mb: 2 }}
                                    placeholder="path/to/notebook.ipynb"
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    label="Project Name"
                                    value={deployForm.project_name}
                                    onChange={(e) => setDeployForm({ ...deployForm, project_name: e.target.value })}
                                    sx={{ mb: 2 }}
                                />
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <FormControl fullWidth sx={{ mb: 2 }}>
                                    <InputLabel>Framework</InputLabel>
                                    <Select
                                        value={deployForm.framework}
                                        onChange={(e) => setDeployForm({ ...deployForm, framework: e.target.value })}
                                    >
                                        <MenuItem value="custom_python">Custom Python</MenuItem>
                                        <MenuItem value="scikit_learn">Scikit-Learn</MenuItem>
                                        <MenuItem value="tensorflow">TensorFlow</MenuItem>
                                        <MenuItem value="pytorch">PyTorch</MenuItem>
                                        <MenuItem value="fastapi">FastAPI</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <TextField
                                    fullWidth
                                    label="Version"
                                    value={deployForm.version}
                                    onChange={(e) => setDeployForm({ ...deployForm, version: e.target.value })}
                                    sx={{ mb: 2 }}
                                />
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <FormControl fullWidth sx={{ mb: 2 }}>
                                    <InputLabel>Rate Limit Tier</InputLabel>
                                    <Select
                                        value={deployForm.rate_limit_tier}
                                        onChange={(e) => setDeployForm({ ...deployForm, rate_limit_tier: e.target.value })}
                                    >
                                        <MenuItem value="free">Free (100/h)</MenuItem>
                                        <MenuItem value="standard">Standard (1,000/h)</MenuItem>
                                        <MenuItem value="premium">Premium (10,000/h)</MenuItem>
                                        <MenuItem value="enterprise">Enterprise (100,000/h)</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>
                        </Grid>

                        {/* Advanced Configuration */}
                        <Accordion sx={{ mt: 2 }}>
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Typography>Advanced Configuration</Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} md={4}>
                                        <TextField
                                            fullWidth
                                            label="CPU Limit (cores)"
                                            type="number"
                                            value={deployForm.cpu_limit}
                                            onChange={(e) => setDeployForm({ ...deployForm, cpu_limit: parseFloat(e.target.value) })}
                                            sx={{ mb: 2 }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={4}>
                                        <TextField
                                            fullWidth
                                            label="Memory Limit (MB)"
                                            type="number"
                                            value={deployForm.memory_limit}
                                            onChange={(e) => setDeployForm({ ...deployForm, memory_limit: parseInt(e.target.value) })}
                                            sx={{ mb: 2 }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={4}>
                                        <TextField
                                            fullWidth
                                            label="Timeout (seconds)"
                                            type="number"
                                            value={deployForm.timeout_seconds}
                                            onChange={(e) => setDeployForm({ ...deployForm, timeout_seconds: parseInt(e.target.value) })}
                                            sx={{ mb: 2 }}
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <FormControlLabel
                                            control={
                                                <Switch
                                                    checked={deployForm.auth_required}
                                                    onChange={(e) => setDeployForm({ ...deployForm, auth_required: e.target.checked })}
                                                />
                                            }
                                            label="Require API Key Authentication"
                                        />
                                    </Grid>
                                </Grid>
                            </AccordionDetails>
                        </Accordion>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeployDialog(false)}>Cancel</Button>
                    <Button
                        onClick={handleDeploy}
                        variant="contained"
                        disabled={!deployForm.name || !deployForm.notebook_path || !deployForm.project_name}
                    >
                        Deploy Model
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Model Logs Dialog */}
            <Dialog open={logsDialog} onClose={() => setLogsDialog(false)} maxWidth="lg" fullWidth>
                <DialogTitle>Model Deployment Logs</DialogTitle>
                <DialogContent>
                    <Box sx={{ mt: 1 }}>
                        {modelLogs.map((log, index) => (
                            <Paper key={index} sx={{ p: 2, mb: 1, bgcolor: log.level === 'error' ? 'error.light' : 'background.default' }}>
                                <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                                    <Chip label={log.event_type} size="small" />
                                    <Typography variant="caption">{new Date(log.timestamp).toLocaleString()}</Typography>
                                </Box>
                                <Typography variant="body2">{log.message}</Typography>
                                {log.details && Object.keys(log.details).length > 0 && (
                                    <Box sx={{ mt: 1, p: 1, bgcolor: 'background.paper', borderRadius: 1 }}>
                                        <Typography variant="caption" fontFamily="monospace">
                                            {JSON.stringify(log.details, null, 2)}
                                        </Typography>
                                    </Box>
                                )}
                            </Paper>
                        ))}
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setLogsDialog(false)}>Close</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default ModelServingDashboard;
